import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";

const MfaSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(API_URLS.MFA_STATUS);
        setEnabled(res.data?.mfa_enabled === true);
      } catch {
        // status fetch failure is non-critical; default to not enrolled
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const startEnroll = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.post(API_URLS.MFA_ENROLL);
      setSecret(res.data.secret);
      setOtpauthUri(res.data.otpauth_uri);
      setConfirmCode("");
      setMessage(
        "Chave gerada. Confirme o código da sua aplicação de autenticação.",
      );
    } catch {
      setError("Não foi possível iniciar a ativação. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmCode.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(API_URLS.MFA_CONFIRM, { code: confirmCode });
      setEnabled(true);
      setSecret(null);
      setOtpauthUri(null);
      setMessage("Verificação em dois passos ativada com sucesso.");
    } catch {
      setError("Código inválido. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disableCode.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(API_URLS.MFA_DISABLE, { code: disableCode });
      setEnabled(false);
      setDisableCode("");
      setMessage("Verificação em dois passos desativada.");
    } catch {
      setError("Código inválido. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="box">A carregar…</div>;
  }

  return (
    <div className="box">
      <legend className="title is-4">Segurança</legend>

      {message && (
        <div className="notification is-success is-light">{message}</div>
      )}
      {error && <div className="notification is-danger is-light">{error}</div>}

      {enabled ? (
        <div>
          <p className="mb-3">
            <span className="tag is-success">Ativada</span> A verificação em
            dois passos está ativa na sua conta.
          </p>
          <form onSubmit={disable}>
            <div className="field">
              <label className="label">
                Código de 6 dígitos para desativar
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) =>
                    setDisableCode(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="000000"
                />
              </div>
            </div>
            <button
              className="button is-danger"
              type="submit"
              disabled={busy || disableCode.length !== 6}
            >
              Desativar
            </button>
          </form>
        </div>
      ) : secret && otpauthUri ? (
        <form onSubmit={confirmEnroll}>
          <p className="mb-3">
            Digitalize o código QR abaixo com a sua aplicação de autenticação
            (Google Authenticator, 1Password, …) ou adicione a chave
            manualmente.
          </p>
          <div className="field has-text-centered mb-4">
            <div className="p-3 has-background-white is-inline-block border rounded">
              <QRCodeSVG value={otpauthUri} size={160} level="M" />
            </div>
          </div>
          <div className="field">
            <label className="label">Chave secreta</label>
            <div className="control">
              <code
                className="is-family-monospace has-background-light p-2 is-block"
                style={{ wordBreak: "break-all" }}
              >
                {secret}
              </code>
            </div>
            <a href={otpauthUri} className="is-size-7">
              Abrir diretamente na aplicação
            </a>
          </div>
          <div className="field">
            <label className="label">Código de confirmação</label>
            <div className="control">
              <input
                className="input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={confirmCode}
                onChange={(e) =>
                  setConfirmCode(e.target.value.replace(/\D/g, ""))
                }
                placeholder="000000"
              />
            </div>
          </div>
          <div className="field is-grouped">
            <button
              className="button is-primary"
              type="submit"
              disabled={busy || confirmCode.length !== 6}
            >
              Confirmar e ativar
            </button>
            <button
              className="button"
              type="button"
              onClick={() => {
                setSecret(null);
                setOtpauthUri(null);
                setMessage(null);
                setError(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <div>
          <p className="mb-3">
            Proteja a sua conta com um segundo fator de autenticação.
          </p>
          <button
            className="button is-primary"
            onClick={startEnroll}
            disabled={busy}
          >
            Ativar verificação em dois passos
          </button>
        </div>
      )}
    </div>
  );
};

export default MfaSettings;
